import { Button, Collapse, CollapseProps, InputNumber, Popover, Skeleton, Table, Tooltip } from "antd"
import { Link } from "react-router-dom"
import { useGetBudgetCorrectionQuery, useGetCommentsQuery, useGetInsuranceQuery } from "../../../api/plamo-api"
import Comments, { CommentUsernameDate } from "./comments"
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import dayjs from "dayjs"
import PlamoApiWithSaveNotifications from "../util/with-save-notification"
import { IconPencilSolidFull } from "../../../icon"
import { useGetUsersLightQuery } from "../../../api/auth-api"

const { 
  useCreateBudgetCorrectionWithSaveNotificationMutation,
  useUpdateBudgetCorrectionWithSaveNotificationMutation,
  useDeleteBudgetCorrectionWithSaveNotificationMutation,
  useUpdateCustomerWithSaveNotificationMutation,
} = PlamoApiWithSaveNotifications;

const CommentsCollapseStyle = styled(Collapse)`
  background: none;
  margin: -8px;
  border: none;

  .ant-collapse-item {
    border-radius: 0;
  }
`

const CommentsPanel = ({ customerId }) => {
  const { data } = useGetCommentsQuery({ f: JSON.stringify({ objectType: { c: "EQ", v: "balance" }, objectId: { c: "EQ", v: customerId } }), st: JSON.stringify({ id: 'desc' }) }) as any

  const label = useMemo(() => {
    return <><strong>Notizen{ data?.items?.length ? ':' : '' }</strong> { data?.items?.length ? <><CommentUsernameDate comment={data.items[0]} /> { data.items[0].text }</> : undefined } { data?.items?.length && data.items.length > 1 ? `(+ ${ data.items.length - 1} weitere)` : '' }</>
  }, [data?.items])

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: label,
      children: <Comments objectId={customerId} objectType={"balance"} noHeader />,
    },
  ];

  return <CommentsCollapseStyle items={items} />
}

const PopEnterNumber = ({ title, content, children, onClick, initialValue, onDelete }: any) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [value, setValue] = useState<number | null>(initialValue);
  const [hover, setHover] = useState<boolean>(false);

  const formatter = (value) => {
    const [start, end] = `${value}`.split('.') || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${end ? `${v},${end}` : `${v}`}`;
  };

  const parser = (value) => value?.replace('.', '').replace(',', '.') as unknown as number
  
  const contentBlock = <><div>
    { content }
  </div>
  <InputNumber value={value} formatter={formatter} parser={parser} onChange={setValue} />
  <div style={{ display: "flex", gap: "1em", marginTop: "1em" }}>
    <div style={{ flex: 1 }}></div>
    { onDelete && <Button size="small" onClick={() => { setPopoverOpen(false); onDelete(); } }>Korrektur entfernen</Button> }
    <Button size="small" onClick={() => setPopoverOpen(false)}>Cancel</Button>
    <Button size="small" type="primary" onClick={() => { setPopoverOpen(false); onClick && onClick(value); }}>OK</Button>
  </div>
  </>

  // onDoubleClick={() => { setValue(initialValue); setPopoverOpen(old => !old); }}

  return (<Popover content={contentBlock} title={title} open={popoverOpen}>
    <div style={{ display: "inline-block" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
    { children }
    {hover && <>&nbsp;<span onClick={() => { setValue(initialValue); setPopoverOpen(old => !old); }}><IconPencilSolidFull /></span></>}
    </div>
  </Popover>)
}

const months = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez"
]

const monthsShort = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec"
]

const InsuranceField = ({ insuranceId }) => {
  const { data: insurance } = useGetInsuranceQuery({ id: insuranceId }) as any;
  return <>{ insurance?.name }</>
}

const formatNumberValue = (v:number) => v?.toString().replace('.', ',') || 0

const ValueCell = ({ enabled, budgets, budgetId, customer, year, month}) => {

  const monthBudget = (budgets[customer.id] || {})[month];
  const workTimeBudgetId = `workTime${budgetId.substring(0, 1).toUpperCase()}${budgetId.substring(1)}`
  const [createBudgetCorrection] = useCreateBudgetCorrectionWithSaveNotificationMutation();
  const [updateBudgetCorrection] = useUpdateBudgetCorrectionWithSaveNotificationMutation();
  const [deleteBudgetCorrection] = useDeleteBudgetCorrectionWithSaveNotificationMutation();

  const correctionId = useMemo(() => {
    const correctionBudgetId = `${budgetId}BudgetCorrectionId`
    return (monthBudget || {})[correctionBudgetId];
  }, [monthBudget, budgetId])

  const correction = useGetBudgetCorrectionQuery({ id: correctionId }, { skip: !correctionId }) as any;
  const { data: users } = useGetUsersLightQuery({}) as any;
  const usernameDisplay = useMemo(() => users?.find(u => u.id === correction.data?.updateUserId)?.usernameDisplay || "Unbekannt", [users, correction.data?.updateUserId])
  const dateDisplay = useMemo(() => dayjs(correction.data?.updateTime + 'Z').format('DD.MM.YYYY HH:mm:ss'), [correction.data?.updateTime])
  
  const saveBudgetCorrection = useCallback((value: number) => {
    const monthNumber = monthsShort.indexOf(month) + 1;
    const newBudgetCorrection = {
      customerId: customer.id,
      month: monthNumber,
      year,
      budget: budgetId,
      remainingHours: value,
    }
    if (correctionId) {
      updateBudgetCorrection({ id: correctionId, ...newBudgetCorrection });
    } else {
      createBudgetCorrection(newBudgetCorrection);
    }
  }, [budgetId, customer.id, year, month, createBudgetCorrection, correctionId, updateBudgetCorrection])

  const doDeleteBudgetCorrection = useCallback(() => {
    deleteBudgetCorrection({id: correctionId});
  }, [deleteBudgetCorrection, correctionId])
 
  if(!enabled) {
    return <></>;
  }

  let correctionContent = <>
    { formatNumberValue((monthBudget || {})[workTimeBudgetId]) }<br/>
    ({ formatNumberValue((monthBudget || {})[budgetId]) }) { correctionId && `(b)`}
  </>

  if(correction) {
    correctionContent = <Tooltip title={`Korrektur von ${usernameDisplay} am ${dateDisplay}`}>
      { correctionContent }
    </Tooltip>
  }

  return monthBudget ? <PopEnterNumber title="Wert korrigieren" content={"Bitte verbleibende Stunden zum Monatsende eingeben"} onClick={saveBudgetCorrection} initialValue={(monthBudget || {})[budgetId] || 0} onDelete={correctionId ? doDeleteBudgetCorrection : undefined}>
    { correctionContent }
  </PopEnterNumber> : <Skeleton active paragraph={{ rows: 0, width: 20 }} />;
}

const MinHoursCell = ({ budgets, customer }) => {
  const [updateCustomer] = useUpdateCustomerWithSaveNotificationMutation();
  const budget = budgets[customer.id];
  const isOverwritten = customer.minHoursOverwrite !== null && customer.minHoursOverwrite !== undefined;

  const saveMinHoursOverwrite = useCallback((value: number | null) => {
    updateCustomer({ ...customer, minHoursOverwrite: value });
  }, [updateCustomer, customer])

  const deleteMinHoursOverwrite = useCallback(() => {
    updateCustomer({ ...customer, minHoursOverwrite: null });
  }, [updateCustomer, customer])

  if(customer.unlimitedBudget) {
    return <>∞</>;
  }

  if(!budget) {
    return <Skeleton active paragraph={{ rows: 0, width: 20 }} />;
  }

  return <PopEnterNumber title="Stunden min korrigieren" content={"Bitte minimale Stunden eingeben"} onClick={saveMinHoursOverwrite} initialValue={budget.minHours || 0} onDelete={isOverwritten ? deleteMinHoursOverwrite : undefined}>
    { formatNumberValue(budget.minHours) }{ isOverwritten && <span style={{ fontSize: '50%' }}> (b)</span> }
  </PopEnterNumber>
}

const BalanceCell = ({ budgets, budgetId, customer, year, month }) => {
  month = year === dayjs().year() ? month : 11;
  const budget = (budgets[customer.id] || {})[monthsShort[month]];
  return <>{ (budget || {})[budgetId]?.toString().replace('.', ',') }</>
}

function BalanceSheet({ customers, budgets, year }) {  
  const currentMonth = dayjs().month();
  const currentYear = dayjs().year();

  const monthColumns = useMemo(() => months.map((monthName, index) => ({
    title: monthName,
    dataIndex: `month${index + 1}`,
    key: `month${index + 1}`,
    onCell: (record) => {
      if (record.row === 6) {
        return { colSpan: 0 };
      }
    },
    render: v => (index === dayjs().month() && year === currentYear) ? <strong>{v}</strong> : v,
    width: 65
  })), [currentYear, year]);

  const minHoursSum = useMemo(() => customers
    .filter(customer => !customer.unlimitedBudget)
    .reduce((sum, customer) => sum + (budgets[customer.id]?.minHours || 0), 0), [customers, budgets]);

  const dataSource = customers
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`))
      .map(customer => {
    // Conversion, other and domestic aid only get a row if enabled; the first row is always shown for the expiry label
    const optionalBudgetRows = [
      {
        enabled: customer.conversion,
        budgetId: 'conversion',
        label: 'Umwandlung',
        balance: (budgets[customer.id] || {})[monthsShort[currentMonth]]?.conversion.toString().replace('.', ',')
      },
      {
        enabled: customer.selfFunded,
        budgetId: 'otherBudgetAmount',
        label: 'Sonstiges',
        balance: <BalanceCell budgets={budgets} budgetId="otherBudgetAmount" customer={customer} month={currentMonth} year={year} />
      },
      {
        enabled: customer.domesticAid,
        budgetId: 'domesticAid',
        label: 'Haushaltshilfe',
        balance: <BalanceCell budgets={budgets} budgetId="domesticAid" customer={customer} month={currentMonth} year={year} />
      }
    ]
      .filter(({ enabled }) => enabled)
      .map(({ budgetId, label, balance }) => ({
        budgets: label,
        balance,
        ...Object.fromEntries(monthsShort.map((month, index) => [
          `month${index + 1}`,
          <ValueCell budgets={budgets} budgetId={budgetId} customer={customer} month={month} year={year} enabled />
        ])),
        balancePreviousYear: ''
      }));

    return [
    {
      key: `${customer.id}-1`,
      row: 0,
      id: customer.id,
      customer: `${customer.lastName || ''}, ${customer.firstName || ''}`,
      insurance: <InsuranceField insuranceId={customer.insuranceId} />,
      hoursMin: <MinHoursCell budgets={budgets} customer={customer} />,
      hoursMax: customer.unlimitedBudget ? '∞' : (budgets[customer.id]?.maxHours?.toString().replace('.', ',') || 0),
      budgets: "Gesamt",
      month1: (currentMonth >= 0 || year !== currentYear) ? budgets[customer.id]?.jan.workTimeTotal.toString().replace('.', ',') : '',
      month2: (currentMonth >= 1 || year !== currentYear) ? budgets[customer.id]?.feb.workTimeTotal.toString().replace('.', ',') : '',
      month3: (currentMonth >= 2 || year !== currentYear) ? budgets[customer.id]?.mar.workTimeTotal.toString().replace('.', ',') : '',
      month4: (currentMonth >= 3 || year !== currentYear) ? budgets[customer.id]?.apr.workTimeTotal.toString().replace('.', ',') : '',
      month5: (currentMonth >= 4 || year !== currentYear) ? budgets[customer.id]?.may.workTimeTotal.toString().replace('.', ',') : '',
      month6: (currentMonth >= 5 || year !== currentYear) ? budgets[customer.id]?.jun.workTimeTotal.toString().replace('.', ',') : '',
      month7: (currentMonth >= 6 || year !== currentYear) ? budgets[customer.id]?.jul.workTimeTotal.toString().replace('.', ',') : '',
      month8: (currentMonth >= 7 || year !== currentYear) ? budgets[customer.id]?.aug.workTimeTotal.toString().replace('.', ',') : '',
      month9: (currentMonth >= 8 || year !== currentYear) ? budgets[customer.id]?.sep.workTimeTotal.toString().replace('.', ',') : '',
      month10: (currentMonth >= 9 || year !== currentYear) ? budgets[customer.id]?.oct.workTimeTotal.toString().replace('.', ',') : '',
      month11: (currentMonth >= 10 || year !== currentYear) ? budgets[customer.id]?.nov.workTimeTotal.toString().replace('.', ',') : '',
      month12: (currentMonth >= 11 || year !== currentYear) ? budgets[customer.id]?.dec.workTimeTotal.toString().replace('.', ',') : '',
      balance: (((budgets[customer.id] || {})[monthsShort[currentMonth]]?.supportAmount || 0) + 
        (customer.absenceCare ? (budgets[customer.id] || {})[monthsShort[currentMonth]]?.absenceCare : 0) + 
        (customer.conversion ? (budgets[customer.id] || {})[monthsShort[currentMonth]]?.conversion : 0)).toString().replace('.', ',')
    },
    {
      key: `${customer.id}-2`,
      row: 1,
      customer: `${customer.addressStreet || ''} ${customer.addressHouseNumber || ''}`,
      insurance: customer.socialSecurityNumber,
      budgets: <>Entlastungs-<br/>betrag</>,
      balance: <BalanceCell budgets={budgets} budgetId="supportAmount" customer={customer} month={currentMonth} year={year} />,
      month1: (customer.supportAmountSince || customer.selfFunded) ? (<ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="jan" year={year}  enabled />) : <div style={{ position: 'relative' }}><div style={{ position: 'absolute', color: 'red', fontWeight: 'bold' }}>Kein Startdatum für Entlastungsbetrag angegeben!</div></div>,
      month2: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="feb" year={year} enabled />,
      month3: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="mar" year={year}  enabled />,
      month4: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="apr" year={year}  enabled />,
      month5: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="may" year={year} enabled />,
      month6: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="jun" year={year} enabled />,
      month7: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="jul" year={year} enabled />,
      month8:	<ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="aug" year={year} enabled />,
      month9:	<ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="sep" year={year} enabled />,
      month10: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="oct" year={year} enabled />,
      month11: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="nov" year={year} enabled />,
      month12: <ValueCell budgets={budgets} budgetId="supportAmount" customer={customer} month="dec" year={year} enabled />,
      balancePreviousYear: budgets[customer.id] ? budgets[customer.id]?.previousYear.supportAmount : <Skeleton active paragraph={{ rows: 0 }} />,
    },
    {
      key: `${customer.id}-3`,
      row: 2,
      customer: `${customer.addressZipCode || ''} ${customer.addressCity || ''}`,
      budgets: customer.absenceCare ? (customer.shortTermCare ? <>Verhinderungs-<br/> + Kurzzeitpflege</> : <>Verhinderungs-<br/>pflege</>) : '',
      balance: customer.absenceCare ? <BalanceCell budgets={budgets} budgetId="absenceCare" customer={customer} month={currentMonth} year={year} /> : '',
      month1: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="jan" year={year} enabled={customer.absenceCare} />,
      month2: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="feb" year={year} enabled={customer.absenceCare} />,
      month3: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="mar" year={year} enabled={customer.absenceCare} />,
      month4: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="apr" year={year} enabled={customer.absenceCare} />,
      month5: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="may" year={year} enabled={customer.absenceCare} />,
      month6: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="jun" year={year} enabled={customer.absenceCare} />,
      month7: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="jul" year={year} enabled={customer.absenceCare} />,
      month8:	<ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="aug" year={year} enabled={customer.absenceCare} />,
      month9:	<ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="sep" year={year} enabled={customer.absenceCare} />,
      month10: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="oct" year={year} enabled={customer.absenceCare} />,
      month11: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="nov" year={year} enabled={customer.absenceCare} />,
      month12: <ValueCell budgets={budgets} budgetId="absenceCare" customer={customer} month="dec" year={year} enabled={customer.absenceCare} />,
      balancePreviousYear: ''
    },
    {
      key: `${customer.id}-4`,
      row: 3,
      insurance: 'Es verfallen am 30.06.:',
      hoursMax: '',
      ...optionalBudgetRows[0]
    },
    ...optionalBudgetRows.slice(1).map((budgetRow, index) => ({
      key: `${customer.id}-${index + 5}`,
      row: 4,
      id: customer.id,
      ...budgetRow
    })),
    {
      key: `${customer.id}-7`,
      row: 6,
      id: customer.id,
    }
  ]}).flatMap(c => c);

  const columns: any = [
    {
      title: 'Kunde/Kundin',
      dataIndex: 'customer',
      key: 'customer',
      onCell: (record) => {
        if(record.row === 6) {
          return { colspan: 19 };
        }
      },
      fixed: "left",
      render: (value, customer) => customer.row === 0 ? <Link to={`/customer/${customer.id}`}><strong>{value}</strong></Link> : (customer.row === 6 ? <CommentsPanel customerId={customer.id} /> : value)
    },
    {
      title: 'Versicherung',
      dataIndex: 'insurance',
      key: 'insurance',
      onCell: (record) => {
        if (record.row === 3) {
          return { colspan: 2 };
        } else if(record.row === 6) {
          return { colSpan: 0 };
        } 
      },
      render: (value, record) => record.row === 3 ? <div style={{ textAlign: 'right', fontWeight: 'bold' }}>{value}</div> : value
    },
    {
      title: <>Stunden min<br/><span style={{ fontSize: '120%' }}>Σ { formatNumberValue(Math.round(minHoursSum * 100) / 100) }</span></>,
      dataIndex: "hoursMin",
      key: "hoursMin",
      onCell: (record) => {
        if (record.row === 0) {
          return { rowSpan: 3 };
        } else if (record.row < 4) {
          return { rowSpan: 0 };
        } else if (record.row === 6) {
          return { colSpan: 0 };
        } 
      },
      width: 100,
      render: (value, record) => record.row === 0 ? <div style={{ fontSize: '200%', fontWeight: 'bold', textAlign: 'right', backgroundColor: "#c6e0b4", width: "calc(100% + 16px)", height: "6em", margin: "-8px", padding: "0.2em", paddingTop: "0.75em" }}>{value}</div> : value
    },
    {
      title: "Stunden max",
      dataIndex: "hoursMax",
      key: "hoursMax",
      onCell: (record) => {
        if (record.row === 0) {
          return { rowSpan: 3 };
        } else if (record.row < 3) {
          return { rowSpan: 0 };
        } else if (record.row === 6) {
          return { colSpan: 0 };
        }
      },
      width: 100,
      render: (value, record) => record.row === 0 ? <div style={{ fontSize: '200%', fontWeight: 'bold', textAlign: 'right', backgroundColor: '#92d050', width: "calc(100% + 16px)", height: "6em", margin: "-8px", padding: "0.2em", paddingTop: "0.75em" }}>{value}</div> : (record.row < 6 ? <div style={{ fontWeight: 'bold', textAlign: 'right', backgroundColor: '#92d050', width: "calc(100% + 16px)", margin: "-8px", height: "4.4em", paddingTop: "0.5em" }}>{value}</div> : value),

    },
    {
      title: "Töpfe",
      dataIndex: "budgets",
      key: "budgets",
      render: (value, record) => record.row === 0 ? <strong>{value}</strong> : value,
      onCell: (record) => {
        if (record.row === 6) {
          return { colSpan: 0 };
        }
      },
      width: 120,
    },
    {
      title: <>Gut-<br/>haben</>,
      dataIndex: "balance",
      key: "balance",
      render: (value) => <strong>{value}</strong>,
      onCell: (record) => {
        if (record.row === 6) {
          return { colSpan: 0 };
        }
      },
      width: 60,
    },
    ...monthColumns,
    {
      title: `Guthaben ${year - 1}`,
      dataIndex: "balancePreviousYear",
      key: "balancePreviousYear",
      onCell: (record) => {
        if (record.row === 6) {
          return { colSpan: 0 };
        }
      }
    },
  ];

  const divRef = useRef<any>();

  const [tableHeight, setTableHeight] = useState<number>(50);

  useLayoutEffect(() => {
    setTimeout(() => {
      const height = divRef.current?.offsetHeight;
      console.log("Table height", height)
      setTableHeight(height - 90);
    }, 100);
  }, [setTableHeight])

  return (
    <div ref={divRef} style={{ height: "100%", width: "100%" }}>
      <div style={{ height: "100%", width: "100%", overflowX: "scroll", paddingBottom: "1em" }}>
          <Table 
              size="small" 
              dataSource={dataSource} 
              columns={columns} 
              pagination={false} 
              scroll={{ y: tableHeight, x: 1550 }}
              />
      </div>
    </div>
  )
}

export default BalanceSheet;